import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { sensorAPI } from '../utils/api';
import { ChartDataPoint, SensorHistoryResponse } from '../types';
import { X, TrendingUp, Calendar, Clock, RefreshCw, Download } from 'lucide-react';

interface SensorChartProps {
  sensorId: number;
  sensorName: string;
  sensorType: string;
  onClose: () => void;
}

type TimePeriod = 'today' | 'yesterday' | 'week' | 'month';

const SensorChart: React.FC<SensorChartProps> = ({ sensorId, sensorName, sensorType, onClose }) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('today');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const periods = [
    { value: 'today' as TimePeriod, label: 'Today', icon: Clock },
    { value: 'yesterday' as TimePeriod, label: 'Yesterday', icon: Calendar },
    { value: 'week' as TimePeriod, label: 'Last Week', icon: TrendingUp },
    { value: 'month' as TimePeriod, label: 'Last 30 Days', icon: TrendingUp },
  ];

  const fetchChartData = async (period: TimePeriod, isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const response: SensorHistoryResponse = await sensorAPI.getSensorHistory(sensorId, period);
      setChartData(response.chart_data || []);
    } catch (err: any) {
      console.error('Error fetching chart data:', err);
      setError(err.response?.data?.error || 'Failed to load chart data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchChartData(selectedPeriod);
  }, [sensorId, selectedPeriod]);

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
  };

  const handleRefresh = () => {
    fetchChartData(selectedPeriod, true);
  };

  const handleExport = async () => {
    if (chartData.length === 0) {
      setError('No data available to export');
      return;
    }

    setExporting(true);
    try {
      await sensorAPI.exportSensorHistory(sensorId, selectedPeriod);
      // Success feedback could be added here if needed
    } catch (err: any) {
      console.error('Error exporting data:', err);
      setError(err.response?.data?.error || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const getChartColor = (type: string) => {
    const colors = {
      fuel: '#3b82f6', // blue
      temperature: '#f59e0b', // orange
      pressure: '#8b5cf6', // purple
      vibration: '#eab308', // yellow
      speed: '#10b981', // green
      engine_hours: '#6366f1', // indigo
      gps: '#ec4899', // pink
      other: '#6b7280', // gray
    };
    return colors[type as keyof typeof colors] || colors.other;
  };

  const formatTooltipLabel = (timestamp: string) => {
    try {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        // If timestamp is invalid, return as-is
        return timestamp;
      }
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp || 'N/A';
    }
  };

  const formatValue = (value: number, type: string) => {
    switch (type) {
      case 'fuel':
        return `${value.toFixed(1)}L`;
      case 'temperature':
        return `${value.toFixed(1)}°C`;
      case 'pressure':
        return `${value.toFixed(2)} bar`;
      case 'speed':
        return `${value.toFixed(0)} km/h`;
      case 'engine_hours':
        return `${value.toFixed(0)} hrs`;
      case 'vibration':
        return `${value.toFixed(2)} g`;
      default:
        return value.toFixed(2);
    }
  };

  if (loading) {
    return (
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="bg-white rounded-xl p-8 max-w-md w-full mx-4"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading chart data...</p>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{sensorName}</h2>
              <p className="text-blue-100 capitalize">{sensorType.replace('_', ' ')} Sensor History</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleExport}
                disabled={exporting || chartData.length === 0}
                className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export to Excel"
              >
                <Download className={`w-5 h-5 ${exporting ? 'animate-pulse' : ''}`} />
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                title="Refresh Data"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Period Selection */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-2">
            {periods.map((period) => {
              const IconComponent = period.icon;
              return (
                <button
                  key={period.value}
                  onClick={() => handlePeriodChange(period.value)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedPeriod === period.value
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{period.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart Content */}
        <div className="p-6">
          {error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-4">
                <X className="w-12 h-12 mx-auto mb-2" />
                <p className="text-lg font-medium">Error Loading Data</p>
                <p className="text-sm text-gray-600 mt-2">{error}</p>
              </div>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : chartData.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <TrendingUp className="w-12 h-12 mx-auto mb-2" />
                <p className="text-lg font-medium">No Data Available</p>
                <p className="text-sm text-gray-600 mt-2">
                  No sensor data found for the selected time period.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={getChartColor(sensorType)} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={getChartColor(sensorType)} stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="formatted_time"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    tickFormatter={(value) => formatValue(value, sensorType)}
                  />
                  <Tooltip
                    labelFormatter={(label: string, payload: any[]) => {
                      // Get the full data point from the payload
                      const dataPoint = payload?.[0]?.payload;
                      const timestamp = dataPoint?.timestamp || label;
                      return formatTooltipLabel(timestamp);
                    }}
                    formatter={(value: number) => [formatValue(value, sensorType), 'Value']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={getChartColor(sensorType)}
                    strokeWidth={2}
                    fill="url(#colorGradient)"
                    dot={{ fill: getChartColor(sensorType), strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: getChartColor(sensorType) }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Chart Statistics */}
          {chartData.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Data Points</p>
                <p className="text-xl font-bold text-gray-900">{chartData.length}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Average</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatValue(
                    chartData.reduce((sum, point) => sum + point.value, 0) / chartData.length,
                    sensorType
                  )}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Maximum</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatValue(Math.max(...chartData.map(point => point.value)), sensorType)}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Minimum</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatValue(Math.min(...chartData.map(point => point.value)), sensorType)}
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SensorChart;